import React, { useState, useEffect, useCallback } from 'react';
import { devLog, devWarn, devError } from '../../../utils/devLogger';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { AppColors } from '../../../styles/colors';
import { AppTextStyles } from '../../../styles/textStyles';
import { FormTitle } from '../../../customComponents/FormTitle';
import { AppIdTextField } from '../../../customComponents/AppIdTextField';
import { AppPwdTextField, PwdFieldType } from '../../../customComponents/AppPwdTextField';
import PrimaryButton from '../../../components/PrimaryButton';
import { cmsLogin } from '../../../lib/api/admin/adminApi';
import { useAdminAuth } from '../../../context/AdminAuthContext';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: ${AppColors.background};
  padding: 20px;
`;

const LoginFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 400px;
  gap: 24px;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 16px;
`;

const Logo = styled.img`
  width: 120px;
  height: auto;
`;

const FormWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  margin-top: 8px;
`;

const HelpText = styled.p`
  font-size: ${AppTextStyles.body2.fontSize};
  color: ${AppColors.onInput2};
  text-align: center;
  line-height: 1.5;
  margin: 0;
`;

const CMSLogin: React.FC = () => {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [idError, setIdError] = useState<string | null>(null);
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { login, isLoggedIn, ready } = useAdminAuth();

  useEffect(() => {
    // Context가 완전히 초기화되고, 이미 로그인되어 있고, 현재 로그인 시도 중이 아닐 때만 리다이렉트
    if (ready && isLoggedIn && !isLoading) {
      devLog('이미 로그인되어 있음, CMS 페이지로 이동');
      window.location.href = '/cms/admin';
    }
  }, [ready, isLoggedIn, isLoading]);

  const handleLogin = useCallback(async () => {
    if (isLoading) return; // 이미 로딩 중이면 중복 실행 방지

    const userIdRegex = /^(?=.*[A-Za-z])(?=.*[0-9])[A-Za-z0-9]{6,20}$/;
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[$@$!%*#?~^<>,.&+=])[A-Za-z\d$@$!%*#?~^<>,.&+=]{8,}$/;

    setIdError(null);
    setPwdError(null);

    let hasError = false;

    if (!userId) {
      setIdError("아이디를 입력해주세요.");
      hasError = true;
    } else if (!userIdRegex.test(userId)) {
      setIdError("아이디는 영문과 숫자를 모두 포함하여 6~20자로 입력해주세요.");
      hasError = true;
    }

    if (!password) {
      setPwdError("비밀번호를 입력해주세요.");
      hasError = true;
    } else if (!passwordRegex.test(password)) {
      setPwdError("비밀번호는 영문, 숫자, 특수문자를 모두 포함하여 8자 이상 입력해주세요.");
      hasError = true;
    }

    if (hasError) return;

    setIsLoading(true);

    try {
      const result = await cmsLogin({
        adminId: userId,
        password,
      });

      const response = result.data;
      const headers = result.headers;

      devLog('=== 로그인 응답 분석 ===');
      devLog('statusCode:', response?.statusCode);
      devLog('message:', response?.message);
      devLog('error:', response?.error);

      if (response && response.statusCode === 200) {
        devLog('=== 로그인 성공 - 토큰 추출 시작 ===');
        
        // 서버에서 admin_access_token 헤더로 토큰 전달
        const accessToken = headers?.get('admin_access_token') || 
                           response.data?.admin_access_token;
        
        devLog('=== 토큰 추출 결과 ===');
        devLog('사용 가능한 헤더들:', headers ? Array.from(headers.entries()) : 'none');
        devLog('응답 데이터:', response.data);
        devLog('추출된 토큰:', accessToken ? '발견' : '없음');
        devLog('토큰 길이:', accessToken ? accessToken.length : 0);
        
        if (!accessToken) {
          toast.error('관리자 인증 토큰을 찾을 수 없습니다.', {
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          });
          return;
        }
        
        // 토큰을 컨텍스트에 저장
        login(response.data?.adminId || userId, accessToken);
        
        toast.success("로그인되었습니다.");
        
        // 성공 시에만 페이지 이동
        window.location.href = '/cms/admin';
      } else if (response && response.statusCode === 401) {
        // 401 Unauthorized 처리
        const errorMessage = response.error?.customMessage || 
                            response.message || 
                            "아이디 또는 비밀번호가 일치하지 않습니다.";
        
        devLog('=== 로그인 실패 (401) ===');
        devLog('에러 메시지:', errorMessage);
        
        toast.error(errorMessage, {
          autoClose: 5000, // 5초 동안 표시
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        });
        // 실패 시에는 페이지 이동하지 않음
        return;
      } else {
        // 기타 에러 처리
        const errorMessage = response?.error?.customMessage || 
                            response?.message || 
                            "로그인 중 오류가 발생했습니다.";
        
        devLog('=== 로그인 실패 (기타) ===');
        devLog('statusCode:', response?.statusCode);
        devLog('에러 메시지:', errorMessage);
        
        toast.error(errorMessage, {
          autoClose: 5000, // 5초 동안 표시
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        });
        // 실패 시에는 페이지 이동하지 않음
        return;
      }
    } catch (error: any) {
      devError('=== 로그인 API 호출 실패 ===', error);
      
      // API 응답에서 에러 정보 추출
      let errorMessage = "로그인 중 오류가 발생했습니다. 다시 시도해주세요.";
      
      if (error?.data) {
        // callAdminApi에서 던진 에러인 경우
        const response = error.data;
        if (response.statusCode === 401) {
          errorMessage = response.error?.customMessage || 
                        response.message || 
                        "아이디 또는 비밀번호가 일치하지 않습니다.";
        } else {
          errorMessage = response.error?.customMessage || 
                        response.message || 
                        "로그인 중 오류가 발생했습니다.";
        }
      } else if (error?.response?.data) {
        // axios 에러인 경우
        const response = error.response.data;
        if (response.statusCode === 401) {
          errorMessage = response.error?.customMessage || 
                        response.message || 
                        "아이디 또는 비밀번호가 일치하지 않습니다.";
        } else {
          errorMessage = response.error?.customMessage || 
                        response.message || 
                        "로그인 중 오류가 발생했습니다.";
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      devLog('최종 에러 메시지:', errorMessage);
      toast.error(errorMessage, {
        autoClose: 5000, // 5초 동안 표시
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      });
      
      // 에러 발생 시에는 페이지 이동하지 않음
    } finally {
      setIsLoading(false);
    }
  }, [userId, password, login, isLoading]); // useCallback 의존성 배열 추가

  // 엔터 키 핸들러 추가
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && userId && password && !isLoading) {
      e.preventDefault();
      handleLogin();
    }
  }, [userId, password, isLoading, handleLogin]);

  return (
    <Container className="cms-layout">
      <LoginFormContainer>
        <LogoContainer>
          <Logo src="/logo.png" alt="BidSpace Logo" />
        </LogoContainer>
        
        <FormTitle>로그인</FormTitle>
        
        <FormWrapper>
          <div>
            <AppIdTextField
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              showValidationMessage={false}
              errorMessage={idError || undefined}
            />
          </div>
          
          <div>
            <AppPwdTextField
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown} // 추가
              fieldType={PwdFieldType.PASSWORD}
              showValidationMessage={false}
              errorMessage={pwdError || undefined}
            />
          </div>
        </FormWrapper>
        
        <ButtonWrapper>
          <PrimaryButton 
            onClick={handleLogin}
            disabled={isLoading}
          >
            {isLoading ? "로그인 중..." : "로그인"}
          </PrimaryButton>
        </ButtonWrapper>
        
        <HelpText>
          시스템 계정이 없다면, 관리자에게 문의 바랍니다.
        </HelpText>
      </LoginFormContainer>
    </Container>
  );
};

export default CMSLogin;
